import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sellerRegister, buyerRegister, sendOtp, verifyOtp } from "../../api/auth.api";
import { Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import { validateRegistrationStep1, validateRegistrationStep2, validateRegistrationStep3, validateEmail } from "../../utils/validationUtils";

// Vibration Animation Styles
const vibrationKeyframes = `
  @keyframes vibration {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
  }
  
  .form-vibrate {
    animation: vibration 0.5s ease-in-out;
  }
`;

const Register = ({ closeModal, role = "SELLER" }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Multi-step State
  const [step, setStep] = useState(1);

  const [values, setValues] = useState({
    business_name: "", owner_name: "", gst_number: "",
    city: "", state: "", pincode: "", address: "",
    email: "", otp_email: "",
    phone_number: "", otp_phone: "", password: "",
    confirmPassword: "", business_description: "",
    otp: "", 
    type: "manufacturer"
  });

  const [file, setFile] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);
  const [errors, setErrors] = useState({});
  const [vibrating, setVibrating] = useState(false);

  const handleChange = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const triggerVibration = () => {
    setVibrating(true);
    setTimeout(() => setVibrating(false), 500);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  /* --- OTP LOGIC --- */
  const handleSendOtp = async () => {
    if (!values.email) {
      setErrors({ email: "Email is required" });
      triggerVibration();
      return toast.error("Enter email first");
    }
    if (!validateEmail(values.email)) {
      setErrors({ email: "Please enter a valid email" });
      triggerVibration();
      return toast.error("Please enter a valid email");
    }
    try {
      setLoadingOtp(true);
      const res = await sendOtp({ email: values.email, phone_number: values.phone_number });
      if (res.data.isOk) {
        toast.success("OTP sent successfully");
        setOtpSent(true);
      }
    } catch (e) {
      triggerVibration();
      toast.error(e.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoadingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!values.otp_email) {
      setErrors({ otp_email: "OTP is required" });
      triggerVibration();
      return toast.error("Enter OTP");
    }
    try {
      const res = await verifyOtp({ email: values.email, otp: values.otp_email });
      if (res.data.isOk) {
        toast.success("Email verified");
        setOtpVerified(true);
      }
    } catch (e) {
      triggerVibration();
      toast.error(e.response?.data?.message || "OTP verification failed");
    }
  };

  /* --- NAVIGATION LOGIC --- */
  const handleNext = () => {
    let validationResult;
    
    if (step === 1) {
      validationResult = validateRegistrationStep1(values.email, values.phone_number, values.password, values.confirmPassword, otpVerified);
      if (!validationResult.isValid) {
        setErrors(validationResult.errors);
        triggerVibration();
        const firstError = Object.values(validationResult.errors)[0];
        toast.error(firstError);
        return;
      }
    } else if (step === 2) {
      validationResult = validateRegistrationStep2(values.business_name, values.owner_name, values.gst_number);
      if (!validationResult.isValid) {
        setErrors(validationResult.errors);
        triggerVibration();
        const firstError = Object.values(validationResult.errors)[0];
        toast.error(firstError);
        return;
      }
    }
    
    setErrors({});
    setStep(step + 1);
  };

  const handleBack = () => {
    setErrors({});
    setStep(step - 1);
  };

  /* --- SUBMISSION LOGIC --- */
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    let validationResult;
    
    if (role === "SELLER"  || role === "BUYER") {
      validationResult = validateRegistrationStep3(values.address, values.city, values.pincode, values.state);
      if (!validationResult.isValid) {
        setErrors(validationResult.errors);
        triggerVibration();
        const firstError = Object.values(validationResult.errors)[0];
        toast.error(firstError);
        return;
      }
    }

    try {
      if (role === "SELLER") {
        const formData = new FormData();
        formData.append("business_name", values.business_name);
        formData.append("owner_name", values.owner_name);
        formData.append("gst_number", values.gst_number);
        formData.append("city", values.city);
        formData.append("state", values.state);
        formData.append("pincode", values.pincode);
        formData.append("address", values.address);
        formData.append("email", values.email);
        formData.append("contact_number", values.phone_number);
        formData.append("password", values.password);
        formData.append("business_description", values.business_description);
        formData.append("type", values.type);
        if (file) formData.append("documents", file);
        await sellerRegister(formData);
      } else {
        await buyerRegister({
          name: values.email,
          email: values.email,
          phone_number: values.phone_number,
          password: values.password
        });
      }

      toast.success(role === "SELLER" ? "Registered successfully" : "Signed up successfully");
      closeModal();
      window.dispatchEvent(new Event("openLogin"));
    } catch (error) {
      triggerVibration();
      toast.error(error.response?.data?.message || "Registration failed");
    }
  };

  /* --- STYLING (Preserving your specific clamp logic) --- */
  const containerStyle = {
    maxWidth: "550px", width: "95%", margin: "clamp(15px, 4vw, 30px) auto", 
    backgroundColor: "#fff", borderRadius: "16px", border: "1px solid #e5e7eb",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)", position: "relative",
    display: "flex", flexDirection: "column", overflow: "hidden"
  };

  const headerStyle = { padding: "clamp(20px, 4vw, 30px) clamp(20px, 5vw, 40px) 10px clamp(20px, 5vw, 40px)" };
  const scrollBodyStyle = { padding: "0 clamp(20px, 5vw, 40px) 30px clamp(20px, 5vw, 40px)", maxHeight: "80vh", overflowY: "auto" };
  const inputStyle = {
    backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px",
    padding: "12px 16px", width: "100%", marginBottom: "15px", fontSize: "clamp(13px, 1.2vw, 14px)", outline: "none"
  };

  const progressBarStyle = {
    height: "4px", width: "100%", backgroundColor: "#f1f5f9", borderRadius: "2px", margin: "20px 0", position: "relative"
  };

  const progressFillStyle = {
    height: "100%", width: role === "SELLER" ? `${(step / 3) * 100}%` : "100%",
    backgroundColor: "#112347", borderRadius: "2px", transition: "0.3s ease"
  };

  return (
    <>
      <style>{vibrationKeyframes}</style>
      <div style={containerStyle} className={vibrating ? "form-vibrate" : ""}>
        <div style={headerStyle}>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="fw-bold m-0" style={{ color: "#112347", fontSize: "clamp(20px, 3vw, 24px)" }}>
                {role === "SELLER" ? "Seller Registration" : "Create Account"}
              </h3>
              <p className="text-muted small m-0 mt-1">
                {role === "SELLER" ? `Step ${step} of 3` : "Enter details to get started"}
              </p>
            </div>
            <span onClick={closeModal} style={{ cursor: "pointer", fontSize: "20px" }}>✕</span>
          </div>

          {role === "SELLER" && (
            <div style={progressBarStyle}>
              <div style={progressFillStyle}></div>
            </div>
          )}

          <button 
            onClick={step === 1 ? closeModal : handleBack} 
            className="btn btn-link p-0 text-decoration-none fw-bold mt-2 d-flex align-items-center gap-2" 
            style={{ color: "#112347", fontSize: "14px" }}
          >
            ← {step === 1 ? "Cancel" : "Back to previous step"}
          </button>
        </div>

        <div style={scrollBodyStyle}>
          <form onSubmit={(e) => e.preventDefault()}>
            
            {/* STEP 1: Account & Security */}
            {step === 1 && (
              <div className="animate-fade-in">
                <div style={{ fontSize: "12px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", marginBottom: "15px", marginTop: "20px" }}>
                  Security Credentials
                </div>
                <div className="d-flex gap-2">
                  <div style={{ flex: 1 }}>
                    <input 
                      name="email" 
                      placeholder="Email Address" 
                      style={{
                        ...inputStyle,
                        borderColor: errors.email ? "#ef4444" : "#e2e8f0",
                        backgroundColor: errors.email ? "#fef2f2" : "#f8fafc"
                      }} 
                      value={values.email} 
                      onChange={handleChange} 
                      required 
                      disabled={otpVerified} 
                    />
                    {errors.email && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.email}</p>}
                  </div>
                  {!otpVerified && (
                    <button type="button" className="btn btn-dark fw-bold" style={{ height: "48px", fontSize: "13px", minWidth: "100px" }} onClick={handleSendOtp} disabled={loadingOtp}>
                      {loadingOtp ? "..." : otpSent ? "Resend" : "Send OTP"}
                    </button>
                  )}
                </div>

                {otpSent && !otpVerified && (
                  <div className="d-flex gap-2">
                    <div style={{ flex: 1 }}>
                      <input 
                        name="otp_email" 
                        placeholder="Enter Email OTP" 
                        style={{
                          ...inputStyle,
                          borderColor: errors.otp_email ? "#ef4444" : "#e2e8f0",
                          backgroundColor: errors.otp_email ? "#fef2f2" : "#f8fafc"
                        }} 
                        value={values.otp_email} 
                        onChange={handleChange} 
                      />
                      {errors.otp_email && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.otp_email}</p>}
                    </div>
                    <button type="button" className="btn btn-success fw-bold" style={{ height: "48px", fontSize: "13px" }} onClick={handleVerifyOtp}>Verify</button>
                  </div>
                )}

                {otpVerified && <div className="text-success fw-bold small mb-3">✓ Email verified successfully</div>}

                <input 
                  name="phone_number" 
                  placeholder="Phone Number" 
                  style={{
                    ...inputStyle,
                    borderColor: errors.phone_number ? "#ef4444" : "#e2e8f0",
                    backgroundColor: errors.phone_number ? "#fef2f2" : "#f8fafc"
                  }} 
                  onChange={handleChange} 
                  required 
                />
                {errors.phone_number && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.phone_number}</p>}

                <input 
                  name="password" 
                  type="password" 
                  placeholder="Password" 
                  style={{
                    ...inputStyle,
                    borderColor: errors.password ? "#ef4444" : "#e2e8f0",
                    backgroundColor: errors.password ? "#fef2f2" : "#f8fafc"
                  }} 
                  onChange={handleChange} 
                  required 
                />
                {errors.password && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.password}</p>}

                <input 
                  name="confirmPassword" 
                  type="password" 
                  placeholder="Confirm Password" 
                  style={{
                    ...inputStyle,
                    borderColor: errors.confirmPassword ? "#ef4444" : "#e2e8f0",
                    backgroundColor: errors.confirmPassword ? "#fef2f2" : "#f8fafc"
                  }} 
                  onChange={handleChange} 
                  required 
                />
                {errors.confirmPassword && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.confirmPassword}</p>}

                {role === "SELLER" ? (
                  <button type="button" className="btn w-100 py-3 fw-bold text-white mt-2" style={{ backgroundColor: "#112347" }} onClick={handleNext}>
                    Next: Business Info
                  </button>
                ) : (
                  <button type="button" className="btn w-100 py-3 fw-bold text-white mt-2" style={{ backgroundColor: "#112347" }} onClick={handleSubmit}>
                    Sign Up
                  </button>
                )}
              </div>
            )}

            {/* STEP 2: Business Information (Seller Only) */}
            {step === 2 && role === "SELLER" && (
              <div className="animate-fade-in">
                <div style={{ fontSize: "12px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", marginBottom: "15px", marginTop: "20px" }}>
                  Business Profile
                </div>
                <Row className="g-3">
                  <Col md="6" xs="12">
                    <input 
                      name="business_name" 
                      placeholder="Business Name" 
                      style={{
                        ...inputStyle,
                        borderColor: errors.business_name ? "#ef4444" : "#e2e8f0",
                        backgroundColor: errors.business_name ? "#fef2f2" : "#f8fafc"
                      }} 
                      onChange={handleChange} 
                      required 
                    />
                    {errors.business_name && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-10px" }}>⚠ {errors.business_name}</p>}
                  </Col>
                  <Col md="6" xs="12">
                    <input 
                      name="owner_name" 
                      placeholder="Owner Name" 
                      style={{
                        ...inputStyle,
                        borderColor: errors.owner_name ? "#ef4444" : "#e2e8f0",
                        backgroundColor: errors.owner_name ? "#fef2f2" : "#f8fafc"
                      }} 
                      onChange={handleChange} 
                      required 
                    />
                    {errors.owner_name && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-10px" }}>⚠ {errors.owner_name}</p>}
                  </Col>
                  <Col md="12" xs="12">
                    <input 
                      name="gst_number" 
                      placeholder="GST Number" 
                      style={{
                        ...inputStyle,
                        borderColor: errors.gst_number ? "#ef4444" : "#e2e8f0",
                        backgroundColor: errors.gst_number ? "#fef2f2" : "#f8fafc"
                      }} 
                      onChange={handleChange} 
                      required 
                    />
                    {errors.gst_number && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-10px" }}>⚠ {errors.gst_number}</p>}
                  </Col>
                </Row>

                <label style={{ color: "#112347", fontWeight: "700", fontSize: "14px", marginBottom: "8px", display: "block" }}>Business Type</label>
                <select name="type" value={values.type} onChange={handleChange} style={inputStyle} required>
                  <option value="manufacturer">Manufacturer</option>
                  <option value="reseller">Reseller</option>
                  <option value="hybrid">Hybrid</option>
                </select>

                <button type="button" className="btn w-100 py-3 fw-bold text-white mt-3" style={{ backgroundColor: "#112347" }} onClick={handleNext}>
                  Next: Address & Verification
                </button>
              </div>
            )}

            {/* STEP 3: Location & Documents (Seller Only) */}
            {step === 3 && role === "SELLER" && (
              <div className="animate-fade-in">
                <div style={{ fontSize: "12px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", marginBottom: "15px", marginTop: "20px" }}>
                  Address & Verification
                </div>
                <Row className="g-3">
                  <Col md="6" xs="12">
                    <input 
                      name="city" 
                      placeholder="City" 
                      style={{
                        ...inputStyle,
                        borderColor: errors.city ? "#ef4444" : "#e2e8f0",
                        backgroundColor: errors.city ? "#fef2f2" : "#f8fafc"
                      }} 
                      onChange={handleChange} 
                      required 
                    />
                    {errors.city && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-10px" }}>⚠ {errors.city}</p>}
                  </Col>
                  <Col md="6" xs="12">
                    <input 
                      name="state" 
                      placeholder="State" 
                      style={{
                        ...inputStyle,
                        borderColor: errors.state ? "#ef4444" : "#e2e8f0",
                        backgroundColor: errors.state ? "#fef2f2" : "#f8fafc"
                      }} 
                      onChange={handleChange} 
                      required 
                    />
                    {errors.state && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-10px" }}>⚠ {errors.state}</p>}
                  </Col>
                </Row>

                <input 
                  name="pincode" 
                  placeholder="Pincode" 
                  style={{
                    ...inputStyle,
                    borderColor: errors.pincode ? "#ef4444" : "#e2e8f0",
                    backgroundColor: errors.pincode ? "#fef2f2" : "#f8fafc"
                  }} 
                  onChange={handleChange} 
                  required 
                />
                {errors.pincode && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.pincode}</p>}

                <input 
                  name="address" 
                  placeholder="Full Office Address" 
                  style={{
                    ...inputStyle,
                    borderColor: errors.address ? "#ef4444" : "#e2e8f0",
                    backgroundColor: errors.address ? "#fef2f2" : "#f8fafc"
                  }} 
                  onChange={handleChange} 
                  required 
                />
                {errors.address && <p style={{ color: "#dc2626", fontSize: "12px", marginBottom: "15px", marginTop: "-10px" }}>⚠ {errors.address}</p>}

                <div style={{ fontSize: "12px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", marginBottom: "15px", marginTop: "10px" }}>
                  Documents & Description
                </div>
                <div style={{ border: "1px dashed #cbd5e1", borderRadius: "8px", padding: "20px", textAlign: "center", backgroundColor: "#f8fafc", marginBottom: "20px" }}>
                  <input type="file" accept=".pdf,image/*" onChange={handleFileChange} style={{ width: "100%", fontSize: "12px" }} />
                  <p className="text-muted small mt-2 mb-0">Upload GST Certificate or Business Proof</p>
                </div>

                <textarea name="business_description" placeholder="Short Business Description" style={{ ...inputStyle, height: "100px" }} onChange={handleChange} />

                <button type="button" className="btn w-100 py-3 fw-bold text-white mt-3" style={{ backgroundColor: "#ea580c" }} onClick={handleSubmit}>
                  Submit Registration
                </button>
              </div>
            )}

            <div className="text-center mt-4 small text-muted">
              Already have an account?{" "}
              <span
                onClick={() => { closeModal(); window.dispatchEvent(new Event("openLogin")); }}
                style={{ cursor: "pointer", fontWeight: "bold", color: "#112347" }}
              >
                Login
              </span>
            </div>
          </form>
        </div>

        <style>{`
          .animate-fade-in {
            animation: fadeIn 0.4s ease-in-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </>
  );
};

export default Register;